import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { filter, Subscription } from 'rxjs';

/**
 * Module correlation ID mapping
 * Maps route prefixes to module names for correlation ID grouping
 */
export interface ModuleMapping {
  routePrefix: string;
  moduleName: string;
}

/**
 * Default module mappings based on the app's route structure
 */
const DEFAULT_MODULE_MAPPINGS: ModuleMapping[] = [
  { routePrefix: '/aams-documents', moduleName: 'aams-documents' },
  { routePrefix: '/air-waybill', moduleName: 'air-waybill' },
  { routePrefix: '/track-manifest', moduleName: 'manifest-tracker' },
  { routePrefix: '/manifest-upload', moduleName: 'manifest-upload' },
  { routePrefix: '/shipment-tracking', moduleName: 'shipment-tracker' },
  { routePrefix: '/api-documentation', moduleName: 'api-documentation' },
  { routePrefix: '/contact-sales', moduleName: 'contact-sales' },
  { routePrefix: '/dashboard', moduleName: 'dashboard' },
];

/**
 * Storage keys for correlation ID persistence
 */
const STORAGE_KEYS = {
  CORRELATION_ID: 'correlationId',
  CURRENT_MODULE: 'currentModule',
  MESSAGE_ID: 'messageId',
} as const;

/**
 * CorrelationIdService
 *
 * Manages correlation IDs for tracking related API calls and logs.
 *
 * Correlation IDs are generated:
 * - When navigating to a different module (e.g., from /air-waybill to /contact-sales)
 * - On browser refresh
 * - When explicitly requested
 *
 * The same correlation ID is maintained within a module to group related operations.
 *
 * Usage:
 * ```typescript
 * // In a service or component
 * constructor(private correlationIdService: CorrelationIdService) {
 *   // Get current correlation ID
 *   const id = this.correlationIdService.getCorrelationId();
 *
 *   // Generate a new one for a specific flow
 *   const newId = this.correlationIdService.generateCorrelationId();
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class CorrelationIdService implements OnDestroy {
  private correlationId: string = '';
  private currentModule: string = '';
  private previousUrl: string = '';
  private readonly subscriptions = new Subscription();
  private moduleMappings: ModuleMapping[] = DEFAULT_MODULE_MAPPINGS;
  private browserRefresh = false;
  private restoredFromStorage = false;

  constructor(private readonly router: Router) {
    this.initializeCorrelationId();
    this.setupRouterSubscription();
  }

  /**
   * Initialize correlation ID on service creation
   */
  private initializeCorrelationId(): void {
    // Check for browser refresh
    this.subscriptions.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationStart))
        .subscribe(() => {
          this.browserRefresh = !this.router.navigated;
        })
    );

    // Generate initial correlation ID
    const storedId = sessionStorage.getItem(STORAGE_KEYS.CORRELATION_ID);
    const storedModule = sessionStorage.getItem(STORAGE_KEYS.CURRENT_MODULE);

    if (storedId && storedModule && !this.browserRefresh) {
      this.correlationId = storedId;
      this.currentModule = storedModule;
      this.restoredFromStorage = true;
    } else {
      this.correlationId = this.generateUUID();
      this.persistCorrelationId();
    }
  }

  /**
   * Setup router subscription to track module changes
   */
  private setupRouterSubscription(): void {
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd)
        )
        .subscribe((event) => {
          this.handleNavigation(event.urlAfterRedirects);
        })
    );
  }

  /**
   * Handle navigation and decide if a new correlation ID is needed
   */
  private handleNavigation(newUrl: string): void {
    const newModule = this.getModuleFromUrl(newUrl);
    const previousModule = this.getModuleFromUrl(this.previousUrl);
    const isFirstNavigation = !this.previousUrl;
    const restoredMatchesNewModule =
      this.restoredFromStorage && this.currentModule === newModule;

    // Generate new correlation ID if:
    // - First navigation AND we did NOT restore a matching module from storage
    //   (a same-module refresh keeps the restored ID even though browserRefresh is true)
    // - Subsequent navigation that changes module or somehow flags refresh
    const needNewId = isFirstNavigation
      ? !restoredMatchesNewModule
      : newModule !== previousModule || this.browserRefresh;

    if (needNewId) {
      this.correlationId = this.generateUUID();
      this.currentModule = newModule;
      this.persistCorrelationId();
    }
    this.browserRefresh = false;

    if (isFirstNavigation) {
      this.restoredFromStorage = false;
    }

    this.previousUrl = newUrl;
  }

  /**
   * Get the module name from a URL based on mappings
   */
  private getModuleFromUrl(url: string): string {
    if (!url) {
      return 'unknown';
    }

    for (const mapping of this.moduleMappings) {
      if (url.startsWith(mapping.routePrefix)) {
        return mapping.moduleName;
      }
    }

    // Default to root or unknown
    return url === '/' || url === '' ? 'dashboard' : 'unknown';
  }

  /**
   * Persist correlation ID to session storage
   */
  private persistCorrelationId(): void {
    sessionStorage.setItem(STORAGE_KEYS.CORRELATION_ID, this.correlationId);
    sessionStorage.setItem(STORAGE_KEYS.CURRENT_MODULE, this.currentModule);
  }

  /**
   * Generate a UUID v4
   */
  private generateUUID(): string {
    // Use crypto.randomUUID if available (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // Fallback to manual UUID generation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replaceAll(/[xy]/g, (c) => {
      const r = Math.trunc(Math.random() * 16);
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Get the current correlation ID
   */
  getCorrelationId(): string {
    return this.correlationId;
  }

  /**
   * Get the current module name
   */
  getCurrentModule(): string {
    return this.currentModule;
  }

  /**
   * Set a specific correlation ID (useful for flows started externally)
   */
  setCorrelationId(id: string): void {
    this.correlationId = id;
    this.persistCorrelationId();
  }

  /**
   * Generate a new correlation ID and return it
   * This replaces the current correlation ID
   */
  generateCorrelationId(): string {
    this.correlationId = this.generateUUID();
    this.persistCorrelationId();
    return this.correlationId;
  }

  /**
   * Generate a message ID (unique per request)
   */
  generateMessageId(): string {
    const messageId = this.generateUUID();
    sessionStorage.setItem(STORAGE_KEYS.MESSAGE_ID, messageId);
    return messageId;
  }

  /**
   * Get the current message ID
   */
  getMessageId(): string {
    return sessionStorage.getItem(STORAGE_KEYS.MESSAGE_ID) || this.generateMessageId();
  }

  /**
   * Configure custom module mappings
   */
  configureModuleMappings(mappings: ModuleMapping[]): void {
    this.moduleMappings = [...mappings, ...DEFAULT_MODULE_MAPPINGS];
  }

  /**
   * Add a single module mapping
   */
  addModuleMapping(mapping: ModuleMapping): void {
    this.moduleMappings.unshift(mapping);
  }

  /**
   * Force a new correlation ID for the current module
   * Useful when starting a new user flow within the same module
   */
  refreshCorrelationId(): string {
    return this.generateCorrelationId();
  }

  /**
   * Clear correlation ID (e.g., on logout)
   */
  clearCorrelationId(): void {
    this.correlationId = '';
    this.currentModule = '';
    sessionStorage.removeItem(STORAGE_KEYS.CORRELATION_ID);
    sessionStorage.removeItem(STORAGE_KEYS.CURRENT_MODULE);
    sessionStorage.removeItem(STORAGE_KEYS.MESSAGE_ID);
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
